import React, { useState, useEffect } from 'react';
import { validateApiKey } from '../../utils/api';
import { Button } from './Button';
import { Input } from './Input';
import { cx, layout, typography } from '../../styles';

interface ApiSettingsProps {
  onSettingsChange?: (settings: { apiKey: string; maxCycles: number }) => void;
}

const ApiSettings: React.FC<ApiSettingsProps> = ({ onSettingsChange }) => {
  const [apiKey, setApiKey] = useState<string>('');
  const [maxCycles, setMaxCycles] = useState<number>(2);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isKeyValid, setIsKeyValid] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info' | null; message: string }>({
    type: null,
    message: '',
  });

  // Track if input is being edited
  const [isEditing, setIsEditing] = useState<boolean>(false);
  
  // Load saved settings on component mount
  useEffect(() => {
    // Only load from localStorage if not already editing
    if (!isEditing) {
      const savedApiKey = localStorage.getItem('openrouter_api_key') || '';
      const savedMaxCycles = Number(localStorage.getItem('max_cycles')) || 2;
      
      setApiKey(savedApiKey);
      setMaxCycles(savedMaxCycles);
      
      // If we have a saved API key, consider it valid (it was validated before saving)
      if (savedApiKey) {
        setIsKeyValid(true);
      }
      
      // Notify parent component of initial settings
      if (onSettingsChange && (savedApiKey || savedMaxCycles !== 2)) {
        onSettingsChange({ apiKey: savedApiKey, maxCycles: savedMaxCycles });
      }
    }
  }, [onSettingsChange, isEditing]);

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Set editing state to true when the user changes the input
    setIsEditing(true);
    setApiKey(e.target.value);
    setFeedback({ type: null, message: '' });
    
    // When the API key changes, reset the validation state and cycles to default
    if (isKeyValid) {
      setIsKeyValid(false);
      setMaxCycles(2); // Reset to default value
      localStorage.removeItem('max_cycles'); // Clear saved value
    }
  };

  // Reset editing state after validation
  const resetEditingState = () => {
    setIsEditing(false);
  };

  const handleMaxCyclesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 1) {
      setMaxCycles(value);
      
      // If we have a valid key, save the max cycles and notify parent
      if (isKeyValid) {
        localStorage.setItem('max_cycles', value.toString());
        if (onSettingsChange) {
          onSettingsChange({ apiKey, maxCycles: value });
        }
      }
    }
  };

  const validateAndSaveApiKey = async () => {
    try {
      setIsValidating(true);
      setFeedback({ type: 'info', message: 'Validating API key...' });
      
      // Skip validation if clearing the API key
      if (!apiKey) {
        localStorage.removeItem('openrouter_api_key');
        localStorage.setItem('max_cycles', maxCycles.toString());
        
        setFeedback({ type: 'success', message: 'Settings saved. Using default API key.' });
        setIsKeyValid(false);
        
        if (onSettingsChange) {
          onSettingsChange({ apiKey: '', maxCycles });
        }
        
        resetEditingState();
        setIsValidating(false);
        return;
      }
      
      // Validate API key
      const isValid = await validateApiKey(apiKey);
      
      if (isValid) {
        // Save API key to localStorage
        localStorage.setItem('openrouter_api_key', apiKey);
        localStorage.setItem('max_cycles', maxCycles.toString());
        
        setFeedback({ type: 'success', message: 'API key validated and saved successfully!' });
        setIsKeyValid(true);
        
        // Notify parent component
        if (onSettingsChange) {
          onSettingsChange({ apiKey, maxCycles });
        }
        
        // Reset editing state after successful validation
        resetEditingState();
      } else {
        setFeedback({ type: 'error', message: 'Invalid API key. Please check and try again.' });
        setIsKeyValid(false);
      }
    } catch (error) {
      console.error('Error validating API key:', error);
      setFeedback({ type: 'error', message: 'Error validating API key. Please try again.' });
      setIsKeyValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className={cx(layout.container.card, "space-y-6")}>
      <div className="space-y-4">
        <div>
          <label htmlFor="api-key" className={cx(typography.styles.subheading, "mb-1 block")}>
            OpenRouter API Key (Optional)
          </label>
          <Input
            id="api-key"
            type="password"
            value={apiKey}
            onChange={handleApiKeyChange}
            placeholder="Enter your OpenRouter API key"
            className="w-full"
          />
          <p className={cx(typography.styles.tiny, "mt-1")}>
            Leave blank to use the default API key. Your personal key allows you to customize max cycles.
          </p>
        </div>
        
        <div className={isKeyValid ? "" : "opacity-50 pointer-events-none"}>
          <label htmlFor="max-cycles" className={cx(typography.styles.subheading, "mb-1 block")}>
            Maximum Cycles (min: 1)
          </label>
          <div className="flex items-center space-x-4">
            <Input
              id="max-cycles"
              type="number"
              min={1}
              value={maxCycles}
              onChange={handleMaxCyclesChange}
              disabled={!isKeyValid}
              className="w-20"
            />
          </div>
          <p className={cx(typography.styles.tiny, "mt-1")}>
            {isKeyValid 
              ? "Higher values may improve response quality but will take longer and consume more tokens."
              : "Save a valid API key first to adjust the number of cycles."}
          </p>
        </div>
        
        {feedback.type && (
          <div
            className={cx(
              "mt-2 p-2 rounded text-sm",
              feedback.type === 'success'
                ? 'bg-success text-primary-inverse'
                : feedback.type === 'error'
                ? 'bg-error text-primary-inverse'
                : 'bg-accent-light text-primary-inverse'
            )}
          >
            {feedback.message}
          </div>
        )}
        
        <div className="flex justify-end">
          <Button
            onClick={validateAndSaveApiKey}
            disabled={isValidating}
            variant="primary"
            shape="rounded"
            size="medium"
            className="bg-accent-main hover:bg-accent text-primary-inverse"
          >
            {isValidating ? 'Validating...' : 'Save API Key'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApiSettings;
